import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Mail, Phone, Trash2, X, MessageSquare, Calendar } from "lucide-react";
import { toast } from "sonner";

const STATUSES = [
    { value: "new", label: "Novo", color: "border-magenta text-magenta" },
    { value: "contacted", label: "Contactado", color: "border-blue-500 text-blue-700" },
    { value: "quoted", label: "Orçamento enviado", color: "border-amber-500 text-amber-700" },
    { value: "won", label: "Ganho", color: "border-emerald-500 text-emerald-700" },
    { value: "lost", label: "Perdido", color: "border-rose-500 text-rose-500" },
];

const PROJECT_LABEL = {
    website: "Website", ecommerce: "E-commerce", platform: "Plataforma",
    automation: "Automatização", integration: "Integração API", other: "Outro",
};

function statusMeta(s) {
    return STATUSES.find((x) => x.value === s) || STATUSES[0];
}


export default function AdminContactRequests() {
    const [list, setList] = useState([]);
    const [open, setOpen] = useState(null);
    const [filter, setFilter] = useState("all");

    const load = () => api.get("/admin/contact-requests").then((r) => setList(r.data));
    useEffect(() => { load(); }, []);

    const updateStatus = async (id, status) => {
        try {
            await api.patch(`/admin/contact-requests/${id}`, { status });
            toast.success("Estado actualizado");
            load();
            setOpen((o) => o && o.id === id ? { ...o, status } : o);
        } catch {
            toast.error("Erro");
        }
    };

    const saveNotes = async (id, admin_notes) => {
        try {
            await api.patch(`/admin/contact-requests/${id}`, { admin_notes });
            toast.success("Notas guardadas");
            load();
        } catch {
            toast.error("Erro");
        }
    };

    const del = async (id) => {
        if (!window.confirm("Eliminar pedido?")) return;
        await api.delete(`/admin/contact-requests/${id}`);
        setOpen(null);
        load();
    };

    const filtered = filter === "all" ? list : list.filter((r) => r.status === filter);
    const counts = list.reduce((acc, r) => { acc[r.status] = (acc[r.status] || 0) + 1; return acc; }, {});

    return (
        <div data-testid="admin-leads-page">
            <div className="mb-6">
                <h1 className="font-display font-black text-4xl tracking-tighter">Pedidos de Orçamento</h1>
                <p className="text-sm text-ink/60 mt-1">{list.length} pedidos no total</p>
            </div>

            {/* Filtros por estado */}
            <div className="flex flex-wrap gap-2 mb-6">
                <button
                    onClick={() => setFilter("all")}
                    className={`border-2 border-ink px-3 py-1.5 text-xs font-bold uppercase tracking-wider ${filter === "all" ? "bg-ink text-white" : "bg-white"}`}
                >
                    Todos ({list.length})
                </button>
                {STATUSES.map((s) => (
                    <button
                        key={s.value}
                        onClick={() => setFilter(s.value)}
                        className={`border-2 border-ink px-3 py-1.5 text-xs font-bold uppercase tracking-wider ${filter === s.value ? "bg-ink text-white" : "bg-white"}`}
                    >
                        {s.label} ({counts[s.value] || 0})
                    </button>
                ))}
            </div>

            {filtered.length === 0 ? (
                <div className="border-2 border-ink bg-white p-12 text-center">
                    <MessageSquare size={32} className="mx-auto mb-3 text-ink/30" />
                    <div className="font-mono text-sm text-ink/60">Sem pedidos {filter !== "all" ? `com estado "${statusMeta(filter).label}"` : "ainda"}.</div>
                </div>
            ) : (
                <div className="border-2 border-ink bg-white">
                    <table className="w-full text-sm">
                        <thead className="bg-paper border-b-2 border-ink">
                            <tr className="text-left">
                                <th className="p-3 font-mono uppercase text-xs">Data</th>
                                <th className="p-3 font-mono uppercase text-xs">Nome</th>
                                <th className="p-3 font-mono uppercase text-xs">Contacto</th>
                                <th className="p-3 font-mono uppercase text-xs">Projecto</th>
                                <th className="p-3 font-mono uppercase text-xs">Estado</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((r) => {
                                const meta = statusMeta(r.status);
                                return (
                                    <tr
                                        key={r.id}
                                        className="border-b border-ink/10 hover:bg-paper cursor-pointer"
                                        onClick={() => setOpen(r)}
                                        data-testid={`lead-${r.id}`}
                                    >
                                        <td className="p-3 font-mono text-xs text-ink/60">{(r.created_at || "").slice(0, 10)}</td>
                                        <td className="p-3 font-bold">
                                            {r.name}
                                            {r.company && <div className="text-[11px] font-mono text-ink/60 font-normal">{r.company}</div>}
                                        </td>
                                        <td className="p-3 text-xs">
                                            <div className="font-mono">{r.email}</div>
                                            {r.phone && <div className="font-mono text-ink/60">{r.phone}</div>}
                                        </td>
                                        <td className="p-3 text-xs font-mono">{PROJECT_LABEL[r.project_type] || "—"}</td>
                                        <td className="p-3">
                                            <span className={`text-[10px] font-mono uppercase px-2 py-1 border-2 ${meta.color}`}>{meta.label}</span>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {open && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setOpen(null)}>
                    <div className="bg-white border-2 border-ink max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                        <div className="border-b-2 border-ink p-4 flex items-center justify-between">
                            <div>
                                <h2 className="font-display font-black text-xl tracking-tighter">{open.name}</h2>
                                <div className="text-xs font-mono text-ink/60 flex items-center gap-1 mt-1">
                                    <Calendar size={11} /> {(open.created_at || "").replace("T", " ").slice(0, 16)}
                                </div>
                            </div>
                            <button onClick={() => setOpen(null)}><X size={18} /></button>
                        </div>

                        <div className="p-6 space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <a href={`mailto:${open.email}`} className="border-2 border-ink p-3 hover:bg-paper">
                                    <div className="label-mono mb-1">Email</div>
                                    <div className="font-mono text-sm flex items-center gap-2"><Mail size={14} /> {open.email}</div>
                                </a>
                                {open.phone && (
                                    <a href={`tel:${open.phone}`} className="border-2 border-ink p-3 hover:bg-paper">
                                        <div className="label-mono mb-1">Telefone</div>
                                        <div className="font-mono text-sm flex items-center gap-2"><Phone size={14} /> {open.phone}</div>
                                    </a>
                                )}
                            </div>
                            <div className="grid grid-cols-2 gap-4 text-sm">
                                <div className="border-2 border-ink p-3">
                                    <div className="label-mono mb-1">Empresa</div>
                                    <div className="font-mono">{open.company || "—"}</div>
                                </div>
                                <div className="border-2 border-ink p-3">
                                    <div className="label-mono mb-1">Tipo de projecto</div>
                                    <div className="font-mono">{PROJECT_LABEL[open.project_type] || "—"}</div>
                                </div>
                                <div className="border-2 border-ink p-3 col-span-2">
                                    <div className="label-mono mb-1">Orçamento estimado</div>
                                    <div className="font-mono">{open.budget || "—"}</div>
                                </div>
                            </div>

                            <div>
                                <div className="label-mono mb-2">Mensagem</div>
                                <div className="border-2 border-ink p-4 bg-paper whitespace-pre-wrap text-sm">{open.message}</div>
                            </div>

                            <div>
                                <div className="label-mono mb-2">Estado</div>
                                <div className="flex flex-wrap gap-2">
                                    {STATUSES.map((s) => (
                                        <button
                                            key={s.value}
                                            onClick={() => updateStatus(open.id, s.value)}
                                            className={`border-2 px-3 py-1.5 text-xs font-bold uppercase tracking-wider ${
                                                open.status === s.value ? "bg-ink text-white border-ink" : `bg-white ${s.color}`
                                            }`}
                                            data-testid={`status-${s.value}`}
                                        >
                                            {s.label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div>
                                <div className="label-mono mb-2">Notas internas</div>
                                <textarea
                                    defaultValue={open.admin_notes || ""}
                                    onBlur={(e) => saveNotes(open.id, e.target.value)}
                                    className="input-edge min-h-[100px]"
                                    placeholder="Apontamentos sobre o cliente, follow-up, etc."
                                    data-testid="notes-textarea"
                                />
                                <p className="text-[11px] font-mono text-ink/50 mt-1">Guarda automaticamente ao sair do campo.</p>
                            </div>
                        </div>

                        <div className="border-t-2 border-ink p-4 flex justify-between">
                            <button onClick={() => del(open.id)} className="border-2 border-rose-500 text-rose-500 px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-rose-500 hover:text-white inline-flex items-center gap-2">
                                <Trash2 size={14} /> Eliminar
                            </button>
                            <button onClick={() => setOpen(null)} className="btn-secondary">Fechar</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
